/**
 * Pulse Sonic Service — Operational Feedback Engine
 *
 * Centralized audio feedback service providing safe, debounced, non-blocking playback
 * using the single canonical authoritative Pulse sonic logo (/pulse-seal.wav).
 *
 * Strict Operational Guarantees:
 * - Authoritative physical asset (/pulse-seal.wav) in /public is the single source of truth.
 * - Used byte-for-byte: no procedural recreation, no synthesized substitutes.
 * - Preloads audio assets safely into memory.
 * - Handles browser autoplay restrictions gracefully; fails silently without surfacing errors.
 * - Cooldown/debounce prevents duplicate playback from rapid clicks or React re-renders.
 * - One action = one playback. Clearly audible at reference level.
 * - Audio is presentation/feedback only; never blocks or determines workflow success.
 * - Respects user preference: Pulse Sounds: Off / Subtle / Full (STORAGE_KEY: allegiant_pulse_sound_mode).
 */

export const PULSE_AUDIO_CANONICAL_PATH = '/pulse-seal.wav';
export const PULSE_AUDIO_SEAL_PATH = PULSE_AUDIO_CANONICAL_PATH;
export const PULSE_AUDIO_SEARCH_PATH = PULSE_AUDIO_CANONICAL_PATH;
export const PULSE_AUDIO_REFINE_PATH = PULSE_AUDIO_CANONICAL_PATH;
export const PULSE_AUDIO_BEACON_PATH = '/pulse-search-beacon.wav';

export const PULSE_SOUND_MODE_KEY = 'allegiant_pulse_sound_mode';
export const PULSE_SOUND_EFFECTS_LEGACY_KEY = 'allegiant_sound_effects_enabled';

export type PulseSoundMode = 'OFF' | 'SUBTLE' | 'FULL';

// In-memory preloaded HTMLAudioElement instances
let preloadedSealAudio: HTMLAudioElement | null = null;
let preloadedSearchAudio: HTMLAudioElement | null = null;
let preloadedRefineAudio: HTMLAudioElement | null = null;
let preloadedBeaconAudio: HTMLAudioElement | null = null;

// In-memory decoded AudioBuffers for zero-latency Web Audio API playback
const audioBuffers: Record<string, AudioBuffer> = {};
let sharedAudioContext: AudioContext | null = null;
let hasBoundUnlockListeners = false;

// Per-channel timestamps for debounce/cooldown protection
let lastSealTimestamp = 0;
let lastSearchTimestamp = 0;
let lastRefineTimestamp = 0;
let lastFaxDingTimestamp = 0;

const DEFAULT_SEAL_COOLDOWN_MS = 450;
const DEFAULT_SEARCH_COOLDOWN_MS = 350;
const DEFAULT_REFINE_COOLDOWN_MS = 250;
const DEFAULT_FAX_DING_COOLDOWN_MS = 600;

/**
 * Get or create the shared AudioContext lazily and safely.
 */
export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!sharedAudioContext) {
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        sharedAudioContext = new AudioCtx();
      }
    } catch {
      sharedAudioContext = null;
    }
  }
  return sharedAudioContext;
}

/**
 * Register one-time user gesture listeners to unlock/resume the AudioContext.
 *
 * Strict Operational Guarantees:
 * - Does NOT autoplay or emit sound merely to unlock.
 * - Resumes AudioContext on the first legitimate user gesture (pointerdown, keydown, touchstart, click).
 * - Automatically unregisters once resumed.
 * - Fails silently if blocked by iframe policy.
 */
export function setupAudioUnlock(): void {
  if (typeof window === 'undefined' || hasBoundUnlockListeners) return;
  hasBoundUnlockListeners = true;

  const unlock = () => {
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
    } catch {
      // Fail silently
    }
    const gestureEvents = ['pointerdown', 'keydown', 'touchstart', 'click'];
    gestureEvents.forEach((evt) => {
      window.removeEventListener(evt, unlock, { capture: true } as EventListenerOptions);
    });
  };

  const gestureEvents = ['pointerdown', 'keydown', 'touchstart', 'click'];
  gestureEvents.forEach((evt) => {
    window.addEventListener(evt, unlock, { capture: true, passive: true });
  });
}

/**
 * Manually resume and unlock AudioContext on an explicit user action (e.g. login button click).
 */
export function unlockPulseAudio(): void {
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  } catch {
    // Fail silently
  }
}

/**
 * Initialize / preload all Pulse audio assets safely into memory.
 */
export function preloadPulseAudio(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // 1. Preload HTMLAudioElement instance for fallback
  try {
    if (!preloadedSealAudio && typeof Audio !== 'undefined') {
      preloadedSealAudio = new Audio(PULSE_AUDIO_CANONICAL_PATH);
      preloadedSealAudio.preload = 'auto';
      preloadedSealAudio.load();
      preloadedSearchAudio = preloadedSealAudio;
      preloadedRefineAudio = preloadedSealAudio;
    }
    if (!preloadedBeaconAudio && typeof Audio !== 'undefined') {
      preloadedBeaconAudio = new Audio(PULSE_AUDIO_BEACON_PATH);
      preloadedBeaconAudio.preload = 'auto';
      preloadedBeaconAudio.load();
    }
  } catch {
    preloadedSealAudio = null;
    preloadedSearchAudio = null;
    preloadedRefineAudio = null;
    preloadedBeaconAudio = null;
  }

  // 2. Pre-fetch and decode AudioBuffer for instantaneous Web Audio playback
  try {
    const ctx = getAudioContext();
    if (ctx && typeof fetch !== 'undefined') {
      if (!audioBuffers['seal']) {
        fetch(PULSE_AUDIO_CANONICAL_PATH)
          .then((res) => {
            if (!res.ok) throw new Error('Asset fetch non-200');
            return res.arrayBuffer();
          })
          .then((arrBuf) => ctx.decodeAudioData(arrBuf))
          .then((decoded) => {
            audioBuffers['canonical'] = decoded;
            audioBuffers['seal'] = decoded;
            audioBuffers['search'] = decoded;
            audioBuffers['refine'] = decoded;
          })
          .catch(() => {
            // Fail silently; HTMLAudioElement fallback remains active
          });
      }
      if (!audioBuffers['beacon']) {
        fetch(PULSE_AUDIO_BEACON_PATH)
          .then((res) => {
            if (!res.ok) throw new Error('Asset fetch non-200');
            return res.arrayBuffer();
          })
          .then((arrBuf) => ctx.decodeAudioData(arrBuf))
          .then((decoded) => {
            audioBuffers['beacon'] = decoded;
          })
          .catch(() => {
            // Fail silently; HTMLAudioElement fallback remains active
          });
      }
    }
  } catch {
    // Fail silently
  }
}

// Eager setup in browser
if (typeof window !== 'undefined') {
  try {
    setupAudioUnlock();
    preloadPulseAudio();
  } catch {
    // Fail silently
  }
}

/**
 * Get current Pulse sound preference mode ('OFF' | 'SUBTLE' | 'FULL').
 * Defaults to 'OFF' per global sound policy.
 */
export function getPulseSoundMode(): PulseSoundMode {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'OFF';
  }
  try {
    const mode = localStorage.getItem(PULSE_SOUND_MODE_KEY);
    if (mode === 'OFF' || mode === 'SUBTLE' || mode === 'FULL') {
      return mode;
    }
    // Backward-compat check with legacy boolean key (only 'true'/'1' explicitly enables sound)
    const legacy = localStorage.getItem(PULSE_SOUND_EFFECTS_LEGACY_KEY);
    if (legacy === 'true' || legacy === '1') {
      return 'FULL';
    }
    return 'OFF';
  } catch {
    return 'OFF';
  }
}

/**
 * Set sound preference mode and persist to localStorage.
 */
export function setPulseSoundMode(mode: PulseSoundMode): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(PULSE_SOUND_MODE_KEY, mode);
    localStorage.setItem(PULSE_SOUND_EFFECTS_LEGACY_KEY, mode === 'OFF' ? 'false' : 'true');
    window.dispatchEvent(new CustomEvent('pulse_sound_mode_changed', { detail: { mode } }));
  } catch {
    // Fail silently
  }
}

/**
 * Toggle global sound between OFF and ON (FULL).
 */
export function togglePulseSound(): PulseSoundMode {
  const current = getPulseSoundMode();
  const next: PulseSoundMode = current === 'OFF' ? 'FULL' : 'OFF';
  setPulseSoundMode(next);
  return next;
}

/**
 * Backward compatibility check for sound enabled.
 */
export function isPulseAudioEnabled(): boolean {
  return getPulseSoundMode() !== 'OFF';
}

export function setPulseAudioEnabled(enabled: boolean): void {
  setPulseSoundMode(enabled ? 'FULL' : 'OFF');
}

export interface PlayPulseCueOptions {
  /** Override cooldown in milliseconds */
  cooldownMs?: number;
  /** Volume level 0.0 - 1.0 */
  volume?: number;
}

/**
 * Internal helper to safely trigger an audio cue with volume scaling according to sound mode.
 * Prefers Web Audio API AudioBufferSourceNode when unlocked; falls back seamlessly to HTMLAudioElement.
 */
function playSafeAudio(
  bufferKey: 'seal' | 'search' | 'refine' | 'beacon',
  preloaded: HTMLAudioElement | null,
  fallbackPath: string,
  baseVolume: number,
  options: PlayPulseCueOptions
): void {
  if (typeof window === 'undefined') return;

  const mode = getPulseSoundMode();
  if (mode === 'OFF') return;

  // Scale volume down if user is in 'SUBTLE' mode
  const modeScale = mode === 'SUBTLE' ? 0.45 : 1.0;
  const targetVolume = (options.volume !== undefined ? options.volume : baseVolume) * modeScale;
  const clampedVolume = Math.max(0, Math.min(1, targetVolume));

  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  // 1. Preferred Web Audio API path (instant, immune to iframe playback race conditions)
  if (ctx && ctx.state === 'running' && audioBuffers[bufferKey]) {
    try {
      const source = ctx.createBufferSource();
      source.buffer = audioBuffers[bufferKey];
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(clampedVolume, ctx.currentTime);
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.start(0);
      return;
    } catch {
      // Fall through to HTMLAudioElement
    }
  }

  // 2. HTMLAudioElement fallback path (authoritative file asset, fails silently on error)
  try {
    const audio = new Audio(fallbackPath);

    try {
      audio.volume = clampedVolume;
    } catch {
      // Ignore volume error
    }

    try {
      audio.currentTime = 0;
    } catch {
      // Ignore seek error if unbuffered
    }

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        // Authoritative asset fails silently — strictly NO synthesized fallback chimes
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Pulse Audio] Authoritative playback suppressed for ${bufferKey}:`, err);
        }
      });
    }
  } catch (err) {
    // Fail silently — strictly NO synthesized fallback chimes
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Pulse Audio] HTMLAudioElement error for ${bufferKey}:`, err);
    }
  }
}

/**
 * 1. Play the authoritative Pulse Completion Seal (/pulse-seal.wav).
 *
 * Reference level = 100% (volume: 1.0).
 * Plays the authoritative uploaded public/pulse-seal.wav byte-for-byte.
 * Absolutely no synthesized substitutes, procedural generators, or generic sounds.
 *
 * Triggered strictly after authoritative success:
 * - Successful interactive login
 * - Successful Mobile SOAP submission / completion
 * - Successful Eval / POC / QPR clinical document finalization
 * - Successful supervisor Approve / co-sign
 * - Successful durable WellSky import commit
 * - Successful Universal Tools actions (Call, Fax, Email, Quick Note)
 * - Temporary successful Justin role-switch QA trigger
 */
export function playPulseSeal(options: PlayPulseCueOptions = {}): void {
  const now = Date.now();
  const cooldown = options.cooldownMs ?? DEFAULT_SEAL_COOLDOWN_MS;
  if (now - lastSealTimestamp < cooldown) return;
  lastSealTimestamp = now;

  playSafeAudio('seal', preloadedSealAudio, PULSE_AUDIO_SEAL_PATH, 1.0, options);
}

/**
 * Backward-compatible alias for existing call sites.
 */
export const playPulseCue = playPulseSeal;

/**
 * 2. Play Pulse Search / Beacon Cue (/pulse-search-beacon.wav).
 *
 * Perceived loudness: Subordinate to Pulse Seal (~45-50% volume).
 * Character:
 * - Crisp bright beacon ping
 * - Clean outgoing signal with expanding ring
 * - Light stereo travel with faint reverberant return and glassy shimmer
 *
 * Triggered ONLY when user explicitly starts an intentional Pulse search/intelligence query.
 * Never triggered on typing, focus, hover, selection, or deterministic filters.
 */
export function playPulseSearchBeacon(options: PlayPulseCueOptions = {}): void {
  const now = Date.now();
  const cooldown = options.cooldownMs ?? DEFAULT_SEARCH_COOLDOWN_MS;
  if (now - lastSearchTimestamp < cooldown) return;
  lastSearchTimestamp = now;

  playSafeAudio('seal', preloadedSealAudio, PULSE_AUDIO_CANONICAL_PATH, 1.0, options);
}

/**
 * 3. Play Pulse Refinement / Precision Drill Cue.
 * Uses the canonical Pulse Seal (/pulse-seal.wav) asset.
 */
export function playPulseRefineDrill(options: PlayPulseCueOptions = {}): void {
  const now = Date.now();
  const cooldown = options.cooldownMs ?? DEFAULT_REFINE_COOLDOWN_MS;
  if (now - lastRefineTimestamp < cooldown) return;
  lastRefineTimestamp = now;

  playSafeAudio('seal', preloadedSealAudio, PULSE_AUDIO_CANONICAL_PATH, 1.0, options);
}

/**
 * 4. Play subtle Incoming Fax notification chime (/pulse-search-beacon.wav).
 *
 * Perceived loudness: Subordinate, soft, restrained (~55% volume).
 * Character:
 * - Short, clean, soft, professional ping (duration ~780ms)
 * - Single notification ding (no repeated loop, no harsh alarm)
 *
 * Strictly respects Pulse Sound ON/OFF preference:
 * - If Sound = OFF: no audio plays
 * - If Sound = SUBTLE: scaled down to ~25% volume
 * - If Sound = FULL: played at ~55% volume
 */
export function playIncomingFaxDing(options: PlayPulseCueOptions = {}): void {
  const now = Date.now();
  const cooldown = options.cooldownMs ?? DEFAULT_FAX_DING_COOLDOWN_MS;
  if (now - lastFaxDingTimestamp < cooldown) return;
  lastFaxDingTimestamp = now;

  playSafeAudio('beacon', preloadedBeaconAudio, PULSE_AUDIO_BEACON_PATH, 0.55, options);
}


